import { useEffect, useMemo, useRef, useState } from "react";
import {
  useCollection,
  useCollections,
  useCurrentUserID,
  useIsOwnSpace,
  useRecordsCount,
} from "@/api/hooks";
import {
  CollectionNode,
  buildCollectionTree,
  collectExpandableKeys,
  flattenVisible,
  pathToCollection,
} from "@/common/collectionTree";
import { HGCollection } from "@/common/types";
import CollectionCreate from "./CollectionCreate";
import CollectionRow from "./CollectionRow";
import PinnedCollections from "./PinnedCollections";
import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
} from "../sidebar";
import { Input } from "../input";
import { useParams } from "react-router";
import CollectionSettingsButton from "./CollectionSettingsButton";
import { ChevronsDownUp, ChevronsUpDown, Search } from "lucide-react";
import { consumePinnedNavigation } from "@/state/pinnedNavigation";
import { useExpandedCollections } from "@/state/collectionsTree";
import { useFitToAllRecords } from "@/common/allRecordsRange";

// Below this count, filtering isn't worth the extra input taking up space.
const SEARCH_THRESHOLD = 20;

// The group header's actions are absolutely positioned, so they are packed
// right-to-left by whichever of them are on screen: a lone action must not sit
// in the slot two others would have occupied.
const ACTION_X = 12;
const ACTION_W = 24;
const actionStyle = (slot: number) => ({ right: ACTION_X + slot * ACTION_W });

/** A match in the search list: a row with no hierarchy around it. */
function flatNode(collection: HGCollection): CollectionNode {
  return {
    collection,
    depth: 0,
    implicit: false,
    implicitPath: [],
    key: collection.id,
    children: [],
    descendantCount: 0,
  };
}

export default function CollectionsList() {
  const { userID, collectionID } = useParams();
  const collections = useCollections();
  const counts = useRecordsCount();
  // The URL carries a slug, so the active row can only be identified by the
  // resolved collection's real id.
  const activeCollectionID = useCollection()?.id;
  const canPin = useIsOwnSpace();
  // Clicking the already-open collection widens the time range to fit all of
  // its records, instead of navigating to the page we are already on.
  const fitToAllRecords = useFitToAllRecords();
  // Open/closed rows are remembered per space, so browsing someone else's
  // collections leaves your own state alone.
  const space = useCurrentUserID() ?? "me";
  const { expanded, toggle, reveal, expandAll, collapseAll } =
    useExpandedCollections(space);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const activeItemRef = useRef<HTMLLIElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const revealedRef = useRef<string | undefined>(undefined);
  const pendingScrollRef = useRef(false);

  const roots = useMemo(() => buildCollectionTree(collections), [collections]);
  const expandableKeys = useMemo(() => collectExpandableKeys(roots), [roots]);

  const trimmedQuery = query.trim().toLowerCase();
  const rows = useMemo(() => {
    if (trimmedQuery) {
      return collections
        .filter((collection) =>
          collection.title.toLowerCase().includes(trimmedQuery),
        )
        .map(flatNode);
    }
    return flattenVisible(roots, expanded);
  }, [collections, roots, expanded, trimmedQuery]);

  useEffect(() => {
    // Roots start collapsed, so the row for the collection just navigated to
    // may not be mounted at all. Keyed on a ref rather than the expanded set:
    // re-running would re-open an ancestor the user has since collapsed on
    // purpose.
    if (!activeCollectionID || roots.length === 0) return;
    if (revealedRef.current === activeCollectionID) return;
    revealedRef.current = activeCollectionID;
    const path = pathToCollection(roots, activeCollectionID);
    if (path.length > 0) reveal(path);
  }, [activeCollectionID, roots, reveal]);

  useEffect(() => {
    // Only jump the scroll position when the selection came from clicking a
    // pinned collection — its match here is likely off-screen. Selecting
    // directly in this list shouldn't yank the scroll position around.
    if (consumePinnedNavigation()) pendingScrollRef.current = true;
  }, [userID, collectionID]);

  useEffect(() => {
    // Deliberately every render: the row cannot be scrolled to until the
    // reveal above has expanded its ancestors and React has mounted it, which
    // is one or more commits after the navigation that asked for the scroll.
    if (!pendingScrollRef.current || !activeItemRef.current) return;
    pendingScrollRef.current = false;
    activeItemRef.current.scrollIntoView({ block: "end" });
  });

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const showSearch = collections.length > SEARCH_THRESHOLD;
  const showCollapseAll = expandableKeys.length > 0 && !trimmedQuery;
  const anyExpanded = expanded.size > 0;
  // Right to left: create, search, collapse-all.
  const searchSlot = canPin ? 1 : 0;
  const collapseSlot = searchSlot + (showSearch ? 1 : 0);

  const handleToggleSearch = () => {
    setSearchOpen((prev) => {
      const next = !prev;
      if (!next) setQuery("");
      return next;
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto">
      {canPin && (
        <PinnedCollections
          activeCollectionID={activeCollectionID}
          onActiveClick={fitToAllRecords}
        />
      )}
      <SidebarGroup className="grow overflow-auto">
        <SidebarGroupLabel className="uppercase tracking-[.1em] font-bold text-[10.5px] h-auto px-4 pt-2 pb-1.5">
          Collections
        </SidebarGroupLabel>
        <CollectionCreate />
        {showSearch && (
          <SidebarGroupAction
            style={actionStyle(searchSlot)}
            aria-label={
              searchOpen ? "Hide collection search" : "Search collections"
            }
            onClick={handleToggleSearch}
          >
            <Search />
          </SidebarGroupAction>
        )}
        {showCollapseAll && (
          <SidebarGroupAction
            style={actionStyle(collapseSlot)}
            aria-label={anyExpanded ? "Collapse all" : "Expand all"}
            onClick={() =>
              anyExpanded ? collapseAll() : expandAll(expandableKeys)
            }
          >
            {anyExpanded ? <ChevronsDownUp /> : <ChevronsUpDown />}
          </SidebarGroupAction>
        )}
        {showSearch && searchOpen && (
          <div className="px-2 pb-1.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search collections..."
                className="h-7 pl-7 text-[13px]"
              />
            </div>
          </div>
        )}
        <SidebarGroupContent className="h-full overflow-auto noscroll">
          <SidebarMenu>
            {rows.length === 0 ? (
              <SidebarMenuItem className="pl-4 text-[13px] text-muted-foreground">
                {trimmedQuery ? "No matches" : "No collections"}
              </SidebarMenuItem>
            ) : (
              rows.map((node) => {
                const isActive = activeCollectionID === node.collection.id;
                return (
                  <CollectionRow
                    key={node.key}
                    node={node}
                    isActive={isActive}
                    isExpanded={expanded.has(node.key)}
                    count={counts[node.collection.id]}
                    canPin={canPin}
                    onToggle={toggle}
                    onActiveClick={fitToAllRecords}
                    ref={isActive ? activeItemRef : undefined}
                  />
                );
              })
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      <SidebarGroup className="gap-2">
        <CollectionSettingsButton />
      </SidebarGroup>
    </div>
  );
}
